import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui';
import { 
  Loader2, 
  MessageSquare, 
  Eye, 
  EyeOff,
  Sparkles,
  Shield,
  CheckCircle,
  LogIn
} from 'lucide-react';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, state } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    const success = await login(email, password);
    if (!success) {
      setError('Invalid email or password');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-primary/10 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements - positioned to avoid content areas */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top left corner elements */}
        <div
          className="absolute opacity-5"
          style={{
            left: '5%',
            top: '10%',
            animation: `float 15s ease-in-out infinite`,
          }}
        >
          <MessageSquare className="h-10 w-10 text-primary" />
        </div>
        <div
          className="absolute opacity-5"
          style={{
            left: '15%',
            top: '5%',
            animation: `float 18s ease-in-out infinite`,
            animationDelay: '2s'
          }}
        >
          <LogIn className="h-8 w-8 text-primary" />
        </div>
        
        {/* Top right corner elements */}
        <div
          className="absolute opacity-5"
          style={{
            right: '8%',
            top: '15%',
            animation: `float 20s ease-in-out infinite`,
            animationDelay: '1s'
          }}
        >
          <Shield className="h-12 w-12 text-primary" />
        </div>
        
        {/* Bottom left corner elements */}
        <div
          className="absolute opacity-5"
          style={{
            left: '10%',
            bottom: '20%',
            animation: `float 16s ease-in-out infinite`,
            animationDelay: '3s'
          }}
        >
          <Sparkles className="h-9 w-9 text-primary" />
        </div>
        
        {/* Bottom right corner elements */}
        <div
          className="absolute opacity-5"
          style={{
            right: '5%',
            bottom: '10%',
            animation: `float 22s ease-in-out infinite`,
            animationDelay: '4s'
          }}
        >
          <MessageSquare className="h-11 w-11 text-primary" />
        </div>
        <div
          className="absolute opacity-5"
          style={{
            right: '15%',
            bottom: '25%',
            animation: `float 19s ease-in-out infinite`,
            animationDelay: '1.5s'
          }}
        >
          <CheckCircle className="h-10 w-10 text-primary" />
        </div>
      </div>
      
      {/* Gradient orbs - also adjusted to be more subtle */}
      <div className="absolute top-0 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      
      <div className="w-full max-w-md mx-auto relative z-10">
        {/* Login form */}
        <div className="bg-white rounded-2xl shadow-2xl border-0 relative overflow-hidden">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 pointer-events-none" />
          
          <div className="relative z-10 p-8">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-primary p-3 rounded-2xl shadow-lg transform hover:scale-110 transition-transform">
                  <MessageSquare className="h-10 w-10 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Welcome Back
              </h1>
              <p className="text-base mt-2 text-gray-600">
                Sign in to continue to TextFlow
              </p>
            </div>
            
            <form className="space-y-5" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm animate-in slide-in-from-top-2">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={state.loading}
                  className="appearance-none relative block w-full px-4 py-2.5 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your email"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <a href="/forgot-password" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={state.loading}
                    className="appearance-none relative block w-full px-4 py-2.5 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded transition-colors"
                />
                <label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer select-none">
                  Remember me
                </label>
              </div>

              <div className="space-y-3">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full h-11 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02]"
                  disabled={state.loading}
                >
                  {state.loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </div>
            </form>
            
            <div className="mt-6 space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <a 
                    href="https://textflow.ca/signup"
                    className="font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    Sign up for free
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          33% {
            transform: translateY(-20px) rotate(5deg);
          }
          66% {
            transform: translateY(10px) rotate(-5deg);
          }
        }
      `}</style>
    </div>
  );
};